/**
 * SortableCard Component
 * Draggable and resizable card for dashboard widgets
 */

import { useRef } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DashboardWidget } from '../types'
import { GridSize, getGridSizeClass } from '../utils/grid-size-utils'
import { useWidgetResize } from '../hooks/use-widget-resize'

interface SortableCardProps {
    widget: DashboardWidget
    children: React.ReactNode
    isLocked: boolean
    onResize?: (id: string, newColSpan: GridSize) => void
}

export const SortableCard = ({
    widget,
    children,
    isLocked,
    onResize,
}: SortableCardProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: widget.id, disabled: isLocked })

    const cardRef = useRef<HTMLDivElement>(null)

    const { isResizing, previewColSpan, resizePosition, handleResizeStart } =
        useWidgetResize({
            widgetId: widget.id,
            currentColSpan: widget.colSpan || 6,
            isLocked,
            onResize,
        })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    }

    if (!widget.isVisible) return null

    const originalColSpan = widget.colSpan || 6
    const displayColSpan = previewColSpan || originalColSpan
    const isResizingAndChanged =
        isResizing && previewColSpan && previewColSpan !== originalColSpan

    return (
        <>
            {/* Ghost placeholder showing original size when resizing */}
            {isResizingAndChanged && resizePosition && (
                <div
                    className={`${getGridSizeClass(originalColSpan)} pointer-events-none`}
                >
                    <div className="h-full w-full border-2 border-dashed border-primary/50 rounded-lg bg-primary/5 min-h-[200px]" />
                </div>
            )}

            <div
                ref={(node) => {
                    setNodeRef(node)
                    if (node) {
                        ;(
                            cardRef as React.MutableRefObject<HTMLDivElement | null>
                        ).current = node
                    }
                }}
                data-tour={`widget-${widget.id}`}
                style={{
                    ...style,
                    // When resizing and changed, position absolute to overlay at original position
                    ...(isResizingAndChanged && resizePosition
                        ? {
                              position: 'absolute' as const,
                              zIndex: 50,
                              top: resizePosition.top,
                              left: resizePosition.left,
                              width: `calc(${(displayColSpan / 12) * 100}% - 8px)`,
                          }
                        : {}),
                }}
                className={`relative ${!(isResizingAndChanged && resizePosition) ? getGridSizeClass(displayColSpan) : ''} ${
                    !isLocked ? 'group cursor-grab active:cursor-grabbing' : ''
                } ${isDragging ? 'opacity-50 border-2 border-dashed border-primary rounded-lg' : ''} ${
                    isResizingAndChanged && resizePosition
                        ? 'opacity-80 shadow-2xl scale-[1.02] transition-shadow'
                        : ''
                }`}
                {...(!isLocked && !isResizing
                    ? { ...attributes, ...listeners }
                    : {})}
            >
                {/* Border overlay when resizing */}
                {isResizingAndChanged && resizePosition && (
                    <div className="absolute inset-0 border-2 border-primary rounded-lg pointer-events-none z-10" />
                )}

                {children}

                {/* Resize handle - bottom right corner */}
                {!isLocked && (
                    <div
                        onMouseDown={(e) => {
                            if (cardRef.current) {
                                handleResizeStart(e, cardRef.current)
                            }
                        }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        className="absolute bottom-1 right-1 w-6 h-6 cursor-se-resize opacity-0 group-hover:opacity-100 transition-opacity z-20"
                        title="Resize"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            className="w-full h-full text-gray-400 hover:text-primary"
                        >
                            <path
                                d="M22 22L12 22M22 22L22 12M22 22L14 14"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                )}

                {/* Resize preview indicator */}
                {isResizing && previewColSpan && (
                    <div className="absolute top-2 right-8 z-20 px-2 py-1 bg-primary text-white text-xs rounded-md shadow-lg">
                        {previewColSpan}/12
                    </div>
                )}
            </div>
        </>
    )
}
